// Per spec/blocks.md §9. Sub-section listing of events with inline CTAs.
//
// **P-037.21 (D-127):** `headerStyle` enum + `eyebrowText` + `state` options
// all dropped. The Block previously carried 3 baked-header arms (eyebrow
// via SubSectionHeader, state via StateBar, none); users now compose a
// standalone `sub-section-heading` Block above this one when they want a
// heading (with the optional state-flag affordance via the
// sub-section-heading's `state` option). StateBar Component deleted in
// the same pass — its only Block-level consumer was the state arm here.
//
// Render is now a single layout: events list inside a 30 px L/R + 30 px
// top padded outer cell. Per-event `dividerAbove: true` (P-037.16 D-116)
// puts a full-width rule above every event including the first.
//
// EventListItem CTA pair: per-event `ctaLabel` constrained to a fixed enum
// (`'Register Now'`, `'Add to Waitlist'`, `'Save the Date'`) per P-037.15
// D-115. `'Save the Date'` renders as plain text (no anchor); the other
// two render as links when `ctaUrl` is set, fall back to text otherwise.
//
// Auto-hide: orchestrator-level skip on `events.length === 0` per D-058.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { eventRow, type EventRowProps } from '../components/event-row'
import { pickDefined } from '../components/helpers'

// P-037.31 fourth post-Phase-Close §A — `'Learn More'` added between
// `'Add to Waitlist'` and `'Save the Date'`. Banks's audit: the
// information-only CTA was missing. Existing dispatch (`'Save the
// Date'` = text-only; everything else with URL = link-arm) handles
// `'Learn More'` automatically.
const CTA_LABEL_ENUM = ['Register Now', 'Add to Waitlist', 'Learn More', 'Save the Date'] as const
type CtaLabel = (typeof CTA_LABEL_ENUM)[number]

const eventListItemSchema = z.object({
  title: z.string().min(1),
  dateText: z.string().min(1),
  audience: z.string().min(1).optional(),
  ctaLabel: z.enum(CTA_LABEL_ENUM).optional(),
  ctaUrl: z.string().url().optional(),
})

type EventListItem = z.infer<typeof eventListItemSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  events: z.array(eventListItemSchema).max(6),
})

function eventToProps(item: EventListItem, linkColor: string): EventRowProps {
  const optionals = pickDefined({ audience: item.audience })
  if (item.ctaLabel === undefined) {
    return {
      title: item.title,
      dateText: item.dateText,
      linkColor,
      ...optionals,
    }
  }
  // 'Save the Date' → text-only CTA (ignore ctaUrl). 'Register Now' / 'Add
  // to Waitlist' → link CTA when ctaUrl is set; otherwise fall back to text.
  const isLinkArm: boolean = item.ctaLabel !== 'Save the Date' && item.ctaUrl !== undefined
  if (isLinkArm) {
    return {
      title: item.title,
      dateText: item.dateText,
      ctaLabel: item.ctaLabel,
      ctaUrl: item.ctaUrl as string,
      linkColor,
      ...optionals,
    }
  }
  return {
    title: item.title,
    dateText: item.dateText,
    ctaLabel: item.ctaLabel,
    linkColor,
    ...optionals,
  }
}

export const eventsList = defineBlock({
  id: 'events-list',
  label: 'Events List',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { events: [] },
  autoHide: ({ inputs }) => inputs.events.length === 0,
  render: ({ inputs }, ctx) => {
    // P-037.16: each event row carries `dividerAbove: true` so every
    // event has a leading full-width rule.
    // P-037.18 D-118: eventToProps threads ctx.linkColor through to
    // EventRow's link-arm CTA color.
    // P-037.29 post-Phase-Close §item A revision: the FIRST event row
    // keeps its leading rule but renders FLUSH at the top (paddingTop:
    // 0, paddingBottom: 16) so the rule sits at the Block's leading
    // edge with padding only below. Subsequent rows keep the symmetric
    // 16/16 padding. Banks's verbatim §item A clarification: "the rule
    // should only be above records, not below" + "the first 3 records
    // line should be flush to the top of the block, then some padding
    // to the first line of text." (V1 dropped the first-row rule
    // entirely; revision keeps it + makes it flush instead.)
    // prettier-ignore
    const list = html`${inputs.events.map((event, i) => eventRow({ ...eventToProps(event, ctx.linkColor), dividerAbove: true, dividerAboveFlushTop: i === 0 }))}`
    // P-037.21 D-127 / P-037.29 D-137: single 30 px L/R padded outer
    // cell. Top/bottom padding lives on the system-level inter-Block
    // spacer (`src/render/block-padding.ts`). No baked header (users
    // compose a sub-section-heading Block above for the header).
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${list}</td></tr></table>`
  },
})

// Re-exports for tests
export type { CtaLabel }
